import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import React from 'react';

import { AnimatePresence, FadeSlideIn } from './Animations';
import { cn } from './cn';

export type MessageBarType = 'info' | 'success' | 'warning' | 'error';

const typeConfig: Record<
  MessageBarType,
  { icon: React.ElementType; bg: string; border: string; text: string; iconColor: string }
> = {
  info: {
    icon: InformationCircleIcon,
    bg: 'bg-info-50',
    border: 'border-info-200',
    text: 'text-info-800',
    iconColor: 'text-info-500',
  },
  success: {
    icon: CheckCircleIcon,
    bg: 'bg-success-50',
    border: 'border-success-200',
    text: 'text-success-800',
    iconColor: 'text-success-500',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    bg: 'bg-warning-50',
    border: 'border-warning-200',
    text: 'text-warning-800',
    iconColor: 'text-warning-500',
  },
  error: {
    icon: ExclamationCircleIcon,
    bg: 'bg-danger-50',
    border: 'border-danger-200',
    text: 'text-danger-800',
    iconColor: 'text-danger-500',
  },
};

export interface MessageBarProps {
  type?: MessageBarType;
  children: React.ReactNode;
  onDismiss?: () => void;
  actions?: React.ReactNode;
  className?: string;
}

export const MessageBar = ({ type = 'info', children, onDismiss, actions, className }: MessageBarProps) => {
  const config = typeConfig[type];
  const Icon = config.icon;

  return (
    <FadeSlideIn
      className={cn('flex items-center gap-3 rounded-lg border p-3', config.bg, config.border, className)}
      direction="down"
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
    >
      <Icon className={cn('h-5 w-5 shrink-0', config.iconColor)} />
      <div className={cn('flex-1 text-sm', config.text)}>{children}</div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="shrink-0 rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-black/5 transition-colors duration-200 cursor-pointer bg-transparent border-none"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </FadeSlideIn>
  );
};

export const MessageBarContainer = ({ children }: { children: React.ReactNode }) => (
  <AnimatePresence>{children}</AnimatePresence>
);
